import { createBrowserRouter, redirect, type LoaderFunctionArgs } from 'react-router'

import { lazyIndexRoute, lazyRoute } from '@rdm/shared-ui'

import { ApplicationLoader } from './components/ApplicationLoader'
import { ErrorBoundaryTest } from './components/ErrorBoundaryFallback'
import Sentry from './lib/sentry'
import { loader, Root } from './Root'

const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouterV7(createBrowserRouter)

export const router = sentryCreateBrowserRouter([
  {
    path: '/',
    Component: Root,
    loader,
    HydrateFallback: ApplicationLoader,
    errorElement: <ErrorBoundaryTest />,
    children: [
      lazyIndexRoute(() => import('./pages/dashboard/dashboard'), { id: 'home' }),
      lazyRoute('/my-records', () => import('./pages/dashboard/dashboard'), { id: 'my-records' }),
      lazyRoute('/my-datasets', () => import('./pages/dashboard/dashboard'), { id: 'my-datasets' }),
      lazyRoute('/my-collaborators', () => import('./pages/dashboard/dashboard'), { id: 'my-collaborators' }),
      lazyRoute('/dashboard', () => import('./pages/dashboard/dashboard')),
      lazyRoute('/about', () => import('./pages/about')),
      lazyRoute('/login', () => import('./pages/login')),
      // Legacy UQ-only login entry. Renders the same login page, which selects the UQ SSO
      // provider for this path (see pages/login.tsx).
      lazyRoute('/uq-login', () => import('./pages/login')),
      // Legacy SSO post-login landing. The old app returned from SSO to /login-success?redirectTo=...;
      // the new flow seeds the session from the refresh cookie on any route (see Root loader), so just
      // forward to the intended page. Restricted to internal paths to avoid an open redirect.
      {
        path: '/login-success',
        loader: ({ request }: LoaderFunctionArgs) => {
          const redirectTo = new URL(request.url).searchParams.get('redirectTo')
          const target = redirectTo && redirectTo.startsWith('/') && !redirectTo.startsWith('//') ? redirectTo : '/'
          return redirect(target)
        },
      },
      lazyRoute('/session-expired', () => import('./pages/sessionExpired')),
      // Dedicated pages for /auth rejections (UQ user not yet synced from the CIP, or
      // inactivated). requireAuthLoader redirects here instead of /login, which would
      // bounce straight back through SSO in an endless redirect loop.
      lazyRoute('/unknown-user', () => import('./pages/unknownUser')),
      lazyRoute('/inactive-user', () => import('./pages/inactiveUser')),
      lazyRoute('/ethics-project', () => import('./pages/ethicsProject/ethicsProject')),
      lazyRoute('/resources/learn-the-basics', () => import('./pages/resources/learnTheBasics/learnTheBasics')),
      lazyRoute('/resources/upcoming-training', () => import('./pages/resources/upcomingTraining/upcomingTraining')),
      // /resources/videos has no dedicated page; send it to the resources landing page.
      { path: '/resources/videos', loader: () => redirect('/resources') },
      lazyRoute('/resources', () => import('./pages/resources/resources')),
      lazyRoute('/feedback', () => import('./pages/feedback/feedback')),
      lazyRoute('/updates/:id?', () => import('./pages/updates/updates')),
      // Record routes
      lazyRoute('/create-record/:uuid', () => import('./pages/records/create/view')),
      lazyRoute('/create-record', () => import('./pages/records/create/create')),
      lazyRoute('/record/:uuid', () => import('./pages/records/dashboard/dashboard')),
      lazyRoute('/record/:uuid/publish-dataset', () => import('./pages/datasets/publish/publish')),
      lazyRoute('/join/:joinToken', () => import('./pages/join/join')),
      // Dataset routes
      lazyRoute('/dataset/:uuid', () => import('./pages/datasets/manage/manage')),
      lazyRoute('/dataset/join/:joinToken', () => import('./pages/datasets/join/join')),
      lazyRoute('/files/:uuid', () => import('./pages/files/view/view')),
      lazyRoute('/files/:uuid/download', () => import('./pages/files/download/download')),
      lazyRoute('/files/:uuid/access-request', () => import('./pages/files/accessRequest/accessRequest')),
      // Thesis routes
      lazyRoute('/thesis/dashboard', () => import('./pages/theses/dashboard/dashboard')),
      lazyRoute('/thesis/:id', () => import('./pages/theses/thesisSubmissionDetails/thesisSubmissionDetails')),
      // Legacy role-prefixed thesis deep-links. The old app exposed separate
      // /thesis/principal-advisor/:id, /thesis/chair/:id and /thesis/examiner/:id pages;
      // the current app serves all of them from /thesis/:id (same submissionId key), so
      // redirect the old paths (still live in emails and persisted user notifications).
      {
        path: '/thesis/principal-advisor/:id',
        loader: ({ params }: LoaderFunctionArgs) => redirect(`/thesis/${params.id}`),
      },
      { path: '/thesis/chair/:id', loader: ({ params }: LoaderFunctionArgs) => redirect(`/thesis/${params.id}`) },
      { path: '/thesis/examiner/:id', loader: ({ params }: LoaderFunctionArgs) => redirect(`/thesis/${params.id}`) },
      lazyRoute('/thesis-examiner/join/:joinToken', () => import('./pages/theses/examinerJoin/examinerJoin')),
      // Other
      lazyRoute('/external-landing', () => import('./pages/externalLanding')),
      lazyRoute('/logout', () => import('./pages/logout')),
      lazyRoute('/unauthorized', () => import('./pages/unauthorized')),
      // Admin routes
      {
        path: '/admin',
        children: [
          lazyIndexRoute(() => import('./pages/admin/dashboard/dashboard'), { id: 'admin-home' }),
          lazyRoute('dashboard', () => import('./pages/admin/dashboard/dashboard'), { id: 'admin-dashboard' }),
          // Notification management
          lazyRoute('notifications/create/:id', () => import('./pages/admin/notifications/create'), {
            id: 'admin-notification-clone',
          }),
          lazyRoute('notifications/create', () => import('./pages/admin/notifications/create'), {
            id: 'admin-notification-create',
          }),
          lazyRoute('notifications/:id', () => import('./pages/admin/notifications/update')),
          lazyRoute('notifications', () => import('./pages/admin/notifications/overview')),
          // Update management
          lazyRoute('updates/create', () => import('./pages/admin/updates/create')),
          lazyRoute('updates/:id', () => import('./pages/admin/updates/update')),
          lazyRoute('updates', () => import('./pages/admin/updates/overview')),
          // Feedback management
          lazyRoute('feedback/:id', () => import('./pages/admin/feedback/details')),
          lazyRoute('feedback', () => import('./pages/admin/feedback/overview')),
          // Record management
          lazyRoute('records/:uuid', () => import('./pages/admin/records/details/details')),
          lazyRoute('records', () => import('./pages/admin/records/overview')),
          // Dataset management
          lazyRoute('datasets/:id', () => import('./pages/admin/datasets/details/details')),
          lazyRoute('datasets', () => import('./pages/admin/datasets/overview')),
          // User management
          lazyRoute('users/:id', () => import('./pages/admin/users/details/details')),
          lazyRoute('users', () => import('./pages/admin/users/overview')),
          // Statistics
          lazyRoute('statistics/record', () => import('./pages/admin/statistics/record/record')),
          lazyRoute('statistics/user', () => import('./pages/admin/statistics/user/user')),
          lazyRoute(
            'statistics/organisational-units/:id',
            () => import('./pages/admin/statistics/organisationalUnits/details/details'),
          ),
          lazyRoute(
            'statistics/organisational-units',
            () => import('./pages/admin/statistics/organisationalUnits/organisationalUnits'),
          ),
          lazyRoute('statistics/emails', () => import('./pages/admin/statistics/emails/emails')),
          lazyRoute('statistics/user-sessions', () => import('./pages/admin/statistics/userSessions/userSessions')),
          lazyRoute('statistics/storage', () => import('./pages/admin/statistics/storage/storage')),
          // User Groups
          lazyRoute('user-groups/create', () => import('./pages/admin/userGroups/create')),
          lazyRoute('user-groups/:id', () => import('./pages/admin/userGroups/update')),
          lazyRoute('user-groups', () => import('./pages/admin/userGroups/overview')),
          lazyRoute('reminder-emails', () => import('./pages/admin/reminderEmails/reminderEmails')),
          lazyRoute('developer-console', () => import('./pages/admin/developerConsole/developerConsole')),
          lazyRoute('style-guide', () => import('./pages/admin/styleGuide/styleGuide')),
        ],
      },
      // Graduate School routes
      {
        path: '/graduate-school',
        children: [
          lazyIndexRoute(() => import('./pages/graduateSchool/dashboard/dashboard'), { id: 'grad-school-home' }),
          lazyRoute('dashboard', () => import('./pages/graduateSchool/dashboard/dashboard'), {
            id: 'grad-school-dashboard',
          }),
          lazyRoute(
            'thesis/pending',
            () => import('./pages/graduateSchool/pendingThesisSubmissions/pendingThesisSubmissions'),
            {
              id: 'grad-school-pending-thesis-submissions',
            },
          ),
          lazyRoute(
            'thesis/pending-examiners',
            () => import('./pages/graduateSchool/pendingThesisExaminers/pendingThesisExaminers'),
            {
              id: 'grad-school-pending-thesis-examiners',
            },
          ),
          lazyRoute(
            'thesis/search',
            () => import('./pages/graduateSchool/activeThesisSubmissions/activeThesisSubmissions'),
            {
              id: 'grad-school-active-thesis-submissions',
            },
          ),
          lazyRoute('thesis/completed', () => import('./pages/graduateSchool/completedTheses/completedTheses'), {
            id: 'grad-school-completed-theses',
          }),
          lazyRoute('thesis/candidates', () => import('./pages/graduateSchool/candidates/candidates'), {
            id: 'grad-school-candidates',
          }),
          lazyRoute(
            'thesis/committee-members',
            () => import('./pages/graduateSchool/committeeMembers/committeeMembers'),
            {
              id: 'grad-school-committee-members',
            },
          ),
          lazyRoute('thesis/:id', () => import('./pages/graduateSchool/submissionDetails/submissionDetails'), {
            id: 'grad-school-thesis-submission-details',
          }),
          // Legacy grad-school thesis detail deep-link. The old app used
          // /graduate-school/thesis/details/:id; the current app serves it at
          // /graduate-school/thesis/:id (same id), so redirect the old path.
          {
            path: 'thesis/details/:id',
            loader: ({ params }: LoaderFunctionArgs) => redirect(`/graduate-school/thesis/${params.id}`),
          },
          lazyRoute('users', () => import('./pages/graduateSchool/userManagement/userManagement'), {
            id: 'grad-school-user-management',
          }),
          lazyRoute('thesis/overdue', () => import('./pages/graduateSchool/overdueUserTasks/overdueUserTasks'), {
            id: 'grad-school-overdue-user-tasks',
          }),
          lazyRoute('dean', () => import('./pages/graduateSchool/deans/deans'), {
            id: 'grad-school-dean',
          }),
          lazyRoute('statistics', () => import('./pages/graduateSchool/statistics/statistics'), {
            id: 'grad-school-statistics',
          }),
        ],
      },
      {
        path: '/provost',
        children: [
          lazyIndexRoute(() => import('./pages/provost/dashboard/dashboard'), { id: 'provost-home' }),
          lazyRoute('dashboard', () => import('./pages/provost/dashboard/dashboard'), {
            id: 'provost-dashboard',
          }),
        ],
      },
      // Catch-all 404: render a NotFound page rather than letting React Router throw
      // "No route matches URL" — bots hitting paths like /.gif turned that into Sentry noise.
      lazyRoute('*', () => import('./pages/notFound')),
    ],
  },
])
